import React, { Component, Fragment } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'
import slice from 'lodash/fp/slice'
import map from 'lodash/fp/map'
import join from 'lodash/fp/join'
import last from 'lodash/last'

import prepareComponent from 'client/shared/decorators/prepare-component'
import errorBoundary, {
  ErrorBoundaryProps,
} from 'client/shared/decorators/error-boundary'
import { trimText } from 'client/shared/helpers/text-helpers'
import { authorsAsString } from 'client/bookmate/helpers/author/authorsAsString'
import { getResourceShelvesLoader } from 'client/shared/helpers/resource-helpers'

import HeadTitle from 'client/shared/blocks/head-title'
import Meta from 'client/shared/blocks/meta'
import InfiniteScroll from 'client/shared/blocks/infinite-scroll'
import { Loader } from 'client/shared/blocks/loader'
import Spacer from 'client/shared/blocks/spacer'
import Shelf from 'client/bookmate/blocks/shelf'
import List from 'client/bookmate/blocks/list'

import { getShelvesByIds } from 'client/bookmate/reducers/shelves-reducer'
import { getBookById } from 'client/bookmate/selectors/book-selector'

import { MAX_META_DESCRIPTION_LENGTH } from 'client/shared/constants/metadata'

import { Dispatch, State } from 'shared/types/redux'
import { ShelfProps } from 'client/shared/types/shelf'
import { Params } from 'client/shared/types/react-router'
import { BookSerialProps } from 'client/shared/types/resource'
import { QueryParams } from 'shared/tools/url-helper'
import urlFor from 'shared/tools/url-helper'
import { Helmet } from 'react-helmet-async'

type Props = {
  page: number
  url: string
  domain: string
  currentUserLogin: string
  dispatch: Dispatch
  shelves: ShelfProps[]
  resource: BookSerialProps
  params: Params
  query?: QueryParams
} & ErrorBoundaryProps &
  withI18nProps

const getShelfTitlesStr = compose(join(', '), map('title'), slice(0, 10))

class BookShelvesBox extends Component<Props> {
  static defaultProps = {
    shelves: [],
  }

  load = () => {
    const {
      dispatch,
      params: { uuid },
      page,
      resource: { resourceType },
    } = this.props

    const loadShelves = getResourceShelvesLoader[resourceType]
    return dispatch(loadShelves({ uuid, p: page, pp: 10, append: true }))
  }

  render() {
    const { page, shelves, currentUserLogin, query } = this.props

    return (
      <Fragment>
        {!shelves.length ? (
          <div>
            <Spacer size={32} />
            <Loader color="gray" kind="big" centered />
          </div>
        ) : (
          <InfiniteScroll load={this.load} page={page}>
            {this.renderMeta()}
            <List>
              {shelves.map((shelf, i) => {
                return (
                  <Shelf
                    as="li"
                    query={query}
                    key={i}
                    shelf={shelf}
                    kind="list"
                    showPlus={shelf.creator.login !== currentUserLogin}
                  />
                )
              })}
            </List>
          </InfiniteScroll>
        )}
      </Fragment>
    )
  }

  renderMeta() {
    const {
      resource: {
        title,
        authors,
        resourceType,
        bookshelves_count,
        language,
        uuid,
      },
      i18n,
      domain,
      shelves: shelvesArr,
    } = this.props
    const shelves = getShelfTitlesStr(shelvesArr)
    const headTitle = i18n._(`book.head_title_shelves_${resourceType}`, {
      title,
      authors: authorsAsString(authors),
    })
    const fullDescription = i18n._('book.meta_description_shelves', {
      count: bookshelves_count,
      shelves,
    })
    const description = trimText(fullDescription, MAX_META_DESCRIPTION_LENGTH)

    return (
      <Fragment>
        <Helmet
          link={[
            {
              href:
                urlFor.absoluteRootWithSubdomain(language, domain as string) +
                urlFor.bookShelves(uuid),
              rel: 'canonical',
            },
          ]}
        />
        <HeadTitle>{headTitle}</HeadTitle>
        <Meta name="description" content={description} />
        <Meta name="robots" content="noindex,nofollow" />
      </Fragment>
    )
  }
}

const prepareWrapper = prepareComponent(
  async function ({ store, params, router }) {
    const { uuid = '' } = params
    const { dispatch } = store
    const { resourceType } = last(router.routes)
    const loadShelves = getResourceShelvesLoader[resourceType]
    await dispatch(loadShelves({ uuid, p: 1, pp: 10 }))
  },
  {
    updateParams: 'uuid',
  },
)

const connectWrapper = connect(
  (state: State, ownProps: { route: { resourceType: string } }) => {
    const { resourceType } = ownProps.route
    return {
      shelves: getShelvesByIds(state, state[resourceType].shelves),
      page: state[resourceType].pages.shelves,
      resource: getBookById(state, state[resourceType].uuid),
      currentUserLogin: state.currentUser.data.login,
      query: state.app.storedQuery,
      domain: state.app.domain,
    }
  },
)

const wrappers = compose(
  prepareWrapper,
  connectWrapper,
  errorBoundary,
  withI18n({ update: true }),
)

export default wrappers(BookShelvesBox)
